import { Link } from "react-router-dom";
import { COMPANY, NAV_LINKS } from "@/data";
import { Mail, MapPin, Phone } from "lucide-react";
import Logo from "@/components/Logo";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-neutral-950 text-stone-200 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 pb-16">
          <div className="lg:col-span-5">
            <div className="hu-section-tag !text-amber-500">Get in touch</div>
            <h3 className="font-display text-3xl lg:text-4xl tracking-tight mt-4 mb-6">
              Need a quote, take-off, or technical advice?
            </h3>
            <p className="text-stone-400 leading-relaxed mb-8 max-w-md">
              We supply engineered roof and floor trusses, I-joists, LVL, glulam and connectors
              across Northeastern Ontario. Send us your plans and we'll send back numbers you
              can build with.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/quote"
                data-testid="footer-quote-cta"
                className="inline-flex items-center bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium px-5 py-3 rounded-sm transition-colors"
              >
                Request a Quote
              </Link>
              <a
                href={COMPANY.phoneHref}
                data-testid="footer-phone-link"
                className="inline-flex items-center gap-2 border border-stone-700 hover:border-amber-600 text-sm font-medium px-5 py-3 rounded-sm transition-colors"
              >
                <Phone size={14} /> <span className="hu-ticker">{COMPANY.phone}</span>
              </a>
              <a
                href="mailto:dan@hurontruss.com"
                data-testid="footer-email-dan"
                className="inline-flex items-center gap-2 border border-stone-700 hover:border-stone-500 text-sm font-medium px-5 py-3 rounded-sm transition-colors"
              >
                <Mail size={14} /> dan@hurontruss.com
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="footer-email-link"
                className="inline-flex items-center gap-2 border border-stone-700 hover:border-stone-500 text-sm font-medium px-5 py-3 rounded-sm transition-colors"
              >
                <Mail size={14} /> {COMPANY.email}
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="hu-section-tag !text-amber-500">Navigate</div>
            <ul className="mt-4 space-y-2.5">
              {NAV_LINKS.concat([{ to: "/quote", label: "Request a Quote" }]).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-stone-300 hover:text-amber-400 transition-colors text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="hu-section-tag !text-amber-500">Plants</div>
            <ul className="mt-4 space-y-2.5 text-sm text-stone-300">
              {COMPANY.plants.map((p) => (
                <li key={p.name} className="flex items-center gap-2">
                  <MapPin size={14} className="text-amber-500" />
                  <span className="text-stone-100 font-medium">{p.name}</span>
                  <span className="text-stone-500 text-xs">— {p.region}</span>
                </li>
              ))}
            </ul>
            <div className="hu-section-tag !text-amber-500 mt-8">Service Area</div>
            <ul className="mt-4 space-y-2.5 text-sm text-stone-300">
              {COMPANY.serviceAreas.map((a) => (
                <li key={a} className="flex items-center gap-2">
                  <MapPin size={14} className="text-amber-500" /> {a}
                </li>
              ))}
            </ul>
            <div className="hu-section-tag !text-amber-500 mt-8">Team</div>
            <ul className="mt-4 space-y-3 text-sm">
              {COMPANY.team.map((m) => (
                <li key={m.name}>
                  <div className="text-stone-100 font-medium">{m.name}</div>
                  <div className="text-stone-400 text-xs">{m.role}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-10 pb-8">
          <div className="hu-section-tag !text-amber-500">Memberships</div>
          <div className="mt-5 flex flex-wrap items-center gap-x-10 gap-y-6">
            <a
              href="https://www.oswa.ca"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-membership-oswa"
              className="group inline-flex items-center gap-4 opacity-90 hover:opacity-100 transition-opacity"
              aria-label="Member — Ontario Structural Wood Association"
            >
              <img
                src="/brand/oswa.png"
                alt="Ontario Structural Wood Association"
                className="h-16 w-auto"
                loading="lazy"
              />
              <span className="hidden sm:block text-xs uppercase tracking-widest text-stone-500 group-hover:text-amber-400 transition-colors">
                Member
              </span>
            </a>
            <a
              href="https://www.sbcindustry.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-membership-sbca"
              className="group inline-flex items-center gap-4 opacity-90 hover:opacity-100 transition-opacity"
              aria-label="Member — Structural Building Components Association"
            >
              <img
                src="/brand/sbca.png"
                alt="Structural Building Components Association"
                className="h-16 w-auto"
                loading="lazy"
              />
              <span className="hidden sm:block text-xs uppercase tracking-widest text-stone-500 group-hover:text-amber-400 transition-colors">
                Member
              </span>
            </a>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-12 flex items-center">
          <Logo variant="light" height={160} className="sm:!h-[220px] lg:!h-[300px]" />
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-stone-500">
          <div>© {new Date().getFullYear()} Huron Truss Ltd. — Engineered wood products. Founded {COMPANY.established}.</div>
          <div className="flex gap-5">
            <span>Quality · Service · Experience</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
